import React, { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import styles from './ReportDismissedSection.module.scss';
import { CYPRESS_DATA } from './constants';
import {
  formatSocialSecurityNumber,
  normalizeSocialSecurityNumber,
} from './utils/socialSecurityNumber';

type SelectOption = {
  value: string;
  label: string;
};

type Props = {
  name: string;
  label: string;
  type?: 'text' | 'date' | 'email' | 'tel';
  options?: SelectOption[];
  placeholder?: string;
  hideLabel?: boolean;
  error?: string;
  warning?: string;
  autoFocus?: boolean;
  initialCaretIndex?: number | null;
  dataCy?: string;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
};

const normalizeDateYearToFourDigits = (value: string): string => {
  const sixOrMoreDigitYearMatch = /^(\d{4})\d+-(\d{2})-(\d{2})$/.exec(value);
  if (!sixOrMoreDigitYearMatch) {
    return value;
  }

  return `${sixOrMoreDigitYearMatch[1]}-${sixOrMoreDigitYearMatch[2]}-${sixOrMoreDigitYearMatch[3]}`;
};

const supportsSelectionRange = (
  inputType: 'text' | 'date' | 'email' | 'tel'
): boolean => inputType === 'text' || inputType === 'tel';

const mapSsnDigitIndexToDisplayCaretIndex = (
  digitIndex: number,
  digitCount: number
): number => {
  if (digitCount === 12 && digitIndex > 8) {
    return digitIndex + 1;
  }

  return digitIndex;
};

/** Compact react-hook-form bound control shared by the desktop table and the mobile cards. */
const EmployeeField = ({
  name,
  label,
  type = 'text',
  options,
  placeholder,
  hideLabel = false,
  error,
  warning,
  autoFocus = false,
  initialCaretIndex,
  dataCy,
  onBlur,
  onKeyDown,
}: Props) => {
  const { register, setValue, watch } = useFormContext();
  const field = register(name);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const pendingSsnCaretDigitIndexRef = useRef<number | null>(null);
  const resolvedInputType: 'text' | 'date' | 'email' | 'tel' =
    type === 'email' && initialCaretIndex != null ? 'text' : type;
  const isSocialSecurityNumberField = name.endsWith('.socialSecurityNumber');
  const socialSecurityNumberValue = isSocialSecurityNumberField
    ? formatSocialSecurityNumber(watch(name) as string | undefined)
    : '';

  useLayoutEffect(() => {
    if (
      options ||
      initialCaretIndex == null ||
      !supportsSelectionRange(resolvedInputType)
    ) {
      return;
    }

    const inputElement = inputElementRef.current;
    if (!inputElement) {
      return;
    }

    const clampedCaretIndex = Math.max(
      0,
      Math.min(initialCaretIndex, inputElement.value.length)
    );

    try {
      inputElement.setSelectionRange(clampedCaretIndex, clampedCaretIndex);
      const valueLength = inputElement.value.length;

      // Keep the clicked caret position visible when the value overflows horizontally.
      if (valueLength > 0) {
        const maxScrollLeft = Math.max(
          0,
          inputElement.scrollWidth - inputElement.clientWidth
        );
        const estimatedScrollLeft = Math.max(
          0,
          Math.min(
            maxScrollLeft,
            (clampedCaretIndex / valueLength) * inputElement.scrollWidth -
              inputElement.clientWidth * 0.5
          )
        );

        inputElement.scrollLeft = estimatedScrollLeft;

        requestAnimationFrame(() => {
          inputElement.scrollLeft = estimatedScrollLeft;
          inputElement.setSelectionRange(clampedCaretIndex, clampedCaretIndex);
        });
      }
    } catch {
      // Ignore unsupported input types/runtime quirks and keep plain focus behavior.
    }
  }, [initialCaretIndex, options, resolvedInputType]);

  useLayoutEffect(() => {
    if (!isSocialSecurityNumberField) {
      return;
    }

    const pendingCaretDigitIndex = pendingSsnCaretDigitIndexRef.current;
    if (pendingCaretDigitIndex == null) {
      return;
    }

    const inputElement = inputElementRef.current;
    if (!inputElement) {
      return;
    }

    const ssnDigits = normalizeSocialSecurityNumber(socialSecurityNumberValue);
    const clampedDigitIndex = Math.max(
      0,
      Math.min(pendingCaretDigitIndex, ssnDigits.length)
    );
    const nextCaretIndex = mapSsnDigitIndexToDisplayCaretIndex(
      clampedDigitIndex,
      ssnDigits.length
    );

    try {
      inputElement.setSelectionRange(nextCaretIndex, nextCaretIndex);
    } catch {
      // Ignore unsupported input types/runtime quirks and keep plain focus behavior.
    }

    pendingSsnCaretDigitIndexRef.current = null;
  }, [isSocialSecurityNumberField, socialSecurityNumberValue]);

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    void field.onBlur(event);
    onBlur?.();
  };

  const controlClassName = classNames(styles.cellInput, {
    [styles.cellInputInvalid]: Boolean(error),
  });

  const assignInputRef = (element: HTMLInputElement | null) => {
    field.ref(element);
    inputElementRef.current = element;
  };

  return (
    <div className={styles.field}>
      <label
        className={classNames(styles.fieldLabel, {
          [styles.visuallyHidden]: hideLabel,
        })}
        htmlFor={name}
      >
        {label}
      </label>
      {options ? (
        <select
          {...field}
          id={name}
          className={controlClassName}
          aria-invalid={Boolean(error)}
          autoFocus={autoFocus}
          onChange={(event) => {
            setValue(name, event.target.value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          data-cy={dataCy ?? CYPRESS_DATA.cellInput}
        >
          <option value="">Välj…</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...field}
          ref={assignInputRef}
          id={name}
          type={resolvedInputType}
          inputMode={type === 'email' ? 'email' : undefined}
          autoComplete={type === 'email' ? 'email' : undefined}
          min={resolvedInputType === 'date' ? '1900-01-01' : undefined}
          max={resolvedInputType === 'date' ? '2100-12-31' : undefined}
          className={controlClassName}
          placeholder={placeholder}
          aria-invalid={Boolean(error)}
          autoFocus={autoFocus}
          value={
            isSocialSecurityNumberField ? socialSecurityNumberValue : undefined
          }
          onChange={(event) => {
            const rawInputValue = event.target.value;

            const nextValue = isSocialSecurityNumberField
              ? normalizeSocialSecurityNumber(rawInputValue)
              : rawInputValue;

            if (isSocialSecurityNumberField) {
              const selectionStart = event.target.selectionStart;
              const resolvedSelectionStart =
                selectionStart == null ? rawInputValue.length : selectionStart;
              const digitsBeforeCaret = normalizeSocialSecurityNumber(
                rawInputValue.slice(0, resolvedSelectionStart)
              ).length;

              pendingSsnCaretDigitIndexRef.current = digitsBeforeCaret;
            }

            const normalizedValue =
              resolvedInputType === 'date'
                ? normalizeDateYearToFourDigits(nextValue)
                : nextValue;

            setValue(name, normalizedValue, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          data-cy={dataCy ?? CYPRESS_DATA.cellInput}
        />
      )}
      {error && (
        <span className={styles.cellError} title={error}>
          {error}
        </span>
      )}
      {!error && warning && (
        <span className={styles.cellWarning} title={warning}>
          {warning}
        </span>
      )}
    </div>
  );
};

export default EmployeeField;
